import { Dialog } from 'ogp/providers/dialog-provider/components/buttons.types';
import { Control, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { FormRadioGroup, RadioOption } from 'redesign';
import { QUERY_KEYS } from 'api/query-keys';
import { useDialog } from 'ogp/providers/dialog-provider';
import { shiftAlerts, useAlert } from 'shared/components/alerts';
import { useInvalidateQuery } from 'shared/hooks/query-hooks/useInvalidateQuery';
import { useSiteDetection } from 'shared/hooks';
import {
  ShiftClaimCancellationReason,
  ShiftClaimCancellationReasonLabels,
} from './cancelation-dialog.types';
import { useCancelShiftClaim } from './use-cancel-shift-claim';

export const useGetCancelationDialog = () => {
  const { alertSuccess, alertError } = useAlert();
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const invalidateQuery = useInvalidateQuery();
  const { site } = useSiteDetection();

  const cancelShiftClaim = useCancelShiftClaim(site);
  const { control, handleSubmit } = useForm<{ reason: ShiftClaimCancellationReason }>({
    defaultValues: { reason: ShiftClaimCancellationReason.SICKNESS },
  });

  const handleCancelShiftClaim = async (shiftClaimId: string) => {
    openDialog(
      {
        title: 'Reden van annuleren',
        content: <CancelationDialog control={control} />,
      },
      handleSubmit((data) => {
        disableDialog();
        cancelShiftClaim.mutate(
          {
            cancelationReason: data.reason,
            shiftClaimId: shiftClaimId,
          },
          {
            onSuccess: () => {
              invalidateQuery(QUERY_KEYS.shifts());
              closeDialog();
              alertSuccess(shiftAlerts.success.unsubscribed);
            },
            onError: (error) => {
              alertError(error);
            },
            onSettled: enableDialog,
          }
        );
      })
    );
  };

  return {
    handleCancelShiftClaim,
  };
};

const CancelationDialog = ({
  control,
}: {
  control: Control<
    {
      reason: ShiftClaimCancellationReason;
    },
    any
  >;
}) => {
  const items: RadioOption[] = Object.keys(ShiftClaimCancellationReason).map(
    (cancelationReason) => {
      return {
        label:
          ShiftClaimCancellationReasonLabels[
            cancelationReason as keyof typeof ShiftClaimCancellationReason
          ],
        value: cancelationReason,
      };
    }
  );
  return (
    <Box display="flex" flexDirection="column">
      <Box>
        <FormRadioGroup name="reason" control={control} items={items}></FormRadioGroup>
      </Box>
    </Box>
  );
};

export const getOgpCancelationDialog = (isFreelancerWithinCancelationPeriod: boolean): Dialog => {
  if (isFreelancerWithinCancelationPeriod) {
    return {
      title: 'Let op! Je annuleert de flexwerker binnen het annuleringstermijn',
      content:
        'Hierbij heeft de flexwerker recht op 50% van ingeplande uren volgens de Overeenkomst van Opdracht. Bij bevestiging ga je hiermee akkoord.',
    };
  }
  return {
    title: 'Weet je zeker dat je deze inschrijving wilt annuleren?',
  };
};
